import { FocusEvent, useCallback, useEffect, useRef, useState } from 'react'

const OPEN_DELAY_MS = 300
const CLOSE_DELAY_MS = 250

/**
 * Opening the folded deck while the pointer rests on it, the way a hovercard opens: after a short
 * pause, so a pointer passing over it on its way to the board does not unfold it, and closing a
 * moment after it leaves, so the pointer can cross the gap onto what opened. Keyboard focus opens it
 * at once and Escape closes it. React carries pointer and focus events through portals, so a list
 * opened from inside the deck counts as the deck. `closeDelayMs` lengthens the moment it waits
 * after the pointer leaves, for a deck a stray pointer would otherwise close too eagerly.
 */
export const useDeckPeek = (
  enabled: boolean,
  { closeDelayMs = CLOSE_DELAY_MS }: { closeDelayMs?: number } = {}
) => {
  const [isOpen, setIsOpen] = useState(false)
  const timer = useRef<number | undefined>(undefined)

  const schedule = useCallback((next: boolean, delay: number) => {
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setIsOpen(next), delay)
  }, [])

  useEffect(() => {
    if (!enabled) {
      window.clearTimeout(timer.current)
      setIsOpen(false)
    }
  }, [enabled])

  useEffect(() => () => window.clearTimeout(timer.current), [])

  const close = useCallback(() => {
    window.clearTimeout(timer.current)
    setIsOpen(false)
  }, [])

  const handlers = {
    onMouseEnter: () => enabled && schedule(true, isOpen ? 0 : OPEN_DELAY_MS),
    onMouseLeave: () => enabled && schedule(false, closeDelayMs),
    onFocus: (event: FocusEvent<HTMLElement>) => {
      if (enabled && event.target.matches(':focus-visible')) {
        schedule(true, 0)
      }
    },
    onBlur: (event: FocusEvent<HTMLElement>) => {
      if (enabled && !event.currentTarget.contains(event.relatedTarget as Node | null)) {
        schedule(false, closeDelayMs)
      }
    },
    onKeyDown: (event: { key: string }) => {
      if (event.key === 'Escape') {
        close()
      }
    },
  }

  return { isOpen: enabled && isOpen, open: () => schedule(true, 0), close, handlers }
}
